import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Image, Wand2 } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface PublishTiendaNubeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CatalogItem {
  id: string;
  name: string;
  image: string;
  category: string;
}

const PublishTiendaNubeModal: React.FC<PublishTiendaNubeModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<CatalogItem[]>([]);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Mock catalog data - in real app this would come from API
  const catalogItems = [
    { id: '1', name: 'Producto 1', image: '/placeholder-product.jpg', category: 'Ropa' },
    { id: '2', name: 'Producto 2', image: '/placeholder-product.jpg', category: 'Accesorios' },
    { id: '3', name: 'Producto 3', image: '/placeholder-product.jpg', category: 'Calzado' },
    { id: '4', name: 'Producto 4', image: '/placeholder-product.jpg', category: 'Ropa' },
    { id: '5', name: 'Producto 5', image: '/placeholder-product.jpg', category: 'Accesorios' },
    { id: '6', name: 'Producto 6', image: '/placeholder-product.jpg', category: 'Calzado' },
  ];

  const filteredItems = catalogItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleItemSelect = (item: CatalogItem) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(prev => [...prev, item]);
    }
  };

  const handleItemRemove = (itemId: string) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleGenerate = async () => {
    if (selectedItems.length === 0) {
        alert(t('selectAtLeastOneProduct'));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate generation progress
    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('publishToTiendaNube')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                  placeholder={t('searchImagesPlaceholder')}
                />
              </div>

              {/* Selected Items */}
              {selectedItems.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    {t('selectedImages')} ({selectedItems.length})
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedItems.map((item) => (
                      <div key={item.id} className="relative">
                        <div className="flex items-center space-x-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl p-3">
                          <div className="w-12 h-12 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                            <Image className="w-6 h-6 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">{item.name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{item.category}</p>
                          </div>
                          <button
                            onClick={() => handleItemRemove(item.id)}
                            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Catalog Items */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('catalogArchive')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredItems.map((item) => {
                    const isSelected = selectedItems.find(i => i.id === item.id);
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemSelect(item)}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-300 dark:border-slate-600 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className="w-full h-20 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-3">
                          <Image className="w-8 h-8 text-gray-500" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">{item.category}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                            <span className="text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Instrucciones específicas para la descripción
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="input min-h-[100px] resize-none"
                  placeholder="Describe el tono, estilo, información adicional, o cualquier detalle específico que quieras incluir en las descripciones de los productos..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Las instrucciones ayudarán a la IA a generar descripciones más precisas y atractivas.
                </p>
              </div>

              {/* Generation Progress */}
              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                    <span>Generando descripciones con IA...</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${generationProgress}%` }}
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={isGenerating}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || selectedItems.length === 0}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Wand2 size={16} />
                  <span>{isGenerating ? 'Generando...' : 'Generar descripciones'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublishTiendaNubeModal;
